import React, { useState } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import { TFunction } from "i18next";
import { addYears } from "date-fns";
import MHeader from "@/components/ui/organisms/MHeader";
import headerStyles from "@/components/ui/organisms/MHeader.module.css";
import MSpacer from "@/components/ui/atoms/MSpacer";
import { MButton } from "@/components/ui/atoms/MButtons";
import MTimePicker, {
  parseTimeString,
  formatTimeString,
} from "@/components/ui/atoms/MTimePicker";
import { IconClock, IconRefresh } from "@tabler/icons-react";
import { MemberInfo } from "@/types/member";
import { DEFAULT_YEARS_AHEAD } from "@/utils/constants";
import {
  paintReminderDateField,
  paintReminderEndDateField,
  paintReminderNotesField,
} from "./remindersHelper";
import styles from "./ReminderSummary.module.css";

export interface ReminderSummaryTimeSlot {
  value: string | null;
  onChange: (value: string) => void;
}

interface TimeChipProps extends ReminderSummaryTimeSlot {
  t: TFunction;
}

// A single "Reminder time" slot — a compact, centered pill (time + clock,
// no dose-number badge — Figma 4904-114586), not a bordered MTimeInputField.
// Tapping it opens the time picker directly.
const TimeChip: React.FC<TimeChipProps> = ({ value, onChange, t }) => {
  const [open, setOpen] = useState(false);

  return (
    <>
      <button
        type="button"
        className={styles.timeChip}
        onClick={() => setOpen(true)}
      >
        <span
          className={
            value ? styles.timeChipValue : styles.timeChipValuePlaceholder
          }
        >
          {value || t("common.notAnswered")}
        </span>
        <IconClock size={16} className={styles.timeChipClock} />
      </button>
      <MTimePicker
        open={open}
        onClose={() => setOpen(false)}
        onDone={(date) => {
          onChange(formatTimeString(date, 12));
          setOpen(false);
        }}
        initialValue={parseTimeString(value ?? undefined, 12)}
        title={t("common.selectTime")}
        format={12}
        minuteStep={15}
      />
    </>
  );
};

export interface ReminderSummaryScreenProps {
  memberInfo: MemberInfo;
  title: string;
  description: string;
  /** Namespace passed to the shared date/end-date/notes field helpers —
   *  e.g. "vitals", "medication" — so each reminder type's own i18n copy
   *  (reminders.{labelKey}.reminderDateLabel etc.) is used. */
  labelKey: string;
  /** Recap card at the top of the content: icon + label + a secondary line
   *  (frequency for vitals/medication, "Appointment on {date}" for
   *  appointment types, etc.). */
  recapIcon: React.ReactNode;
  recapIconClass: string;
  recapLabel: string;
  recapSecondaryText: string;
  /** Icon shown before recapSecondaryText — defaults to the refresh icon
   *  (frequency callers). Pass null to omit it entirely (e.g. a plain
   *  "Appointment on ..." line has no icon in Figma). */
  recapSecondaryIcon?: React.ReactNode | null;
  /** Extra class merged onto the secondary line — lets callers override the
   *  default bold/dark styling (e.g. appointment's lighter caption). */
  recapSecondaryTextClassName?: string;
  /** Optional third recap line, plain caption styling, no icon — e.g.
   *  insurance's "Valid until {date}" below the primary holder's name
   *  (Figma 5257-62339). Omit for types with only two recap lines. */
  recapTertiaryText?: string;
  reminderDate: Date | null;
  setReminderDate: (date: Date | null) => void;
  /** Hide the end-date field entirely — one-off appointments have no
   *  recurrence, so there's nothing to end. Defaults to true. */
  showEndDate?: boolean;
  reminderEndDate: Date | null;
  setReminderEndDate: (date: Date | null) => void;
  timeSlots: ReminderSummaryTimeSlot[];
  notes: string | null;
  setNotes: (notes: string | null) => void;
  isInvalid: boolean;
  onSubmit: () => void;
  submitLabel?: string;
  /** Per-type extra rendered inside the white content card, above the
   *  reminder-date field — e.g. medication's "Allow Critical Alerts"
   *  toggle (Figma 5257-62452). Omit for types with nothing extra. */
  topContent?: React.ReactNode;
}

// The shared "final"/summary screen used across every reminder type —
// recap card, start/end date, reminder-time chips, notes, and the submit
// CTA, over the off-white page + curve background. Each reminder type
// supplies its own copy, icon, and recap frequency text; the layout,
// spacing, and field styling are identical across types per Figma.
const ReminderSummaryScreen: React.FC<ReminderSummaryScreenProps> = ({
  memberInfo,
  title,
  description,
  labelKey,
  recapIcon,
  recapIconClass,
  recapLabel,
  recapSecondaryText,
  recapSecondaryIcon,
  recapSecondaryTextClassName,
  recapTertiaryText,
  reminderDate,
  setReminderDate,
  showEndDate = true,
  reminderEndDate,
  setReminderEndDate,
  timeSlots,
  notes,
  setNotes,
  isInvalid,
  onSubmit,
  submitLabel,
  topContent,
}) => {
  const { t } = useTranslation();
  const maxDate = addYears(new Date(), DEFAULT_YEARS_AHEAD);

  return (
    <div className="relative w-full h-full flex flex-col">
      <MHeader
        module="reminders"
        memberInfo={memberInfo}
        title={title}
        description={description}
        // The page-level curve (.summaryPageBackground, applied by the
        // caller to the outer wrapper containing MTopBar + this screen) is
        // the same proven pattern medication's own final stage already
        // uses. stickyBelowTopBarWithGradient gives MHeader the matching
        // opaque purple gradient instead of white, so the header layer
        // blends with the curve instead of sitting on top of it as a
        // visible box — pair with MTopBar's showPurpleGradient on the
        // final stage for the same reason.
        className={headerStyles.stickyBelowTopBarWithGradient}
      />
      {/* MHeader's own .headerContainer already contributes 16px of
          padding-bottom below the description — this 24px spacer makes up
          the rest of the standardized 40px total header-to-body gap used
          across every reminders screen (except the listing screen). */}
      <MSpacer mt={24} />
      {/* No permanent extra bottom clearance beyond .profile-page-root's own
          padding-bottom (see profile.css) — every other reminders stage
          relies on that same padding alone to clear the fixed .confirm-footer
          button; stacking another reserve here unconditionally left a large
          empty gap below the (usually short) summary content. The notes
          field still needs room to scroll clear of the on-screen keyboard
          though (much taller than that padding), so .keyboardClearance adds
          it back only while a field is actually focused. */}
      <div
        className={`profile-input-container flex flex-col gap-4 ${styles.keyboardClearance}`}
      >
        <div className={styles.recapCard}>
          <div className={`${styles.recapIcon} ${recapIconClass}`}>
            {recapIcon}
          </div>
          <div className={styles.recapText}>
            <span className={styles.recapLabel}>{recapLabel}</span>
            {recapSecondaryText && (
              <span
                className={`${styles.recapFrequency} ${
                  recapSecondaryTextClassName || ""
                }`}
              >
                {recapSecondaryIcon === undefined ? (
                  <IconRefresh
                    size={16}
                    className={styles.recapFrequencyIcon}
                  />
                ) : (
                  recapSecondaryIcon
                )}
                {recapSecondaryText}
              </span>
            )}
            {recapTertiaryText && (
              <span className={styles.recapTertiary}>{recapTertiaryText}</span>
            )}
          </div>
        </div>
        <div className={styles.contentCard}>
          {topContent}
          {paintReminderDateField(
            reminderDate,
            setReminderDate,
            maxDate,
            t,
            labelKey,
            true,
            true
          )}
          <div className={styles.timeSlotLabelWrap}>
            <span className={styles.timeSlotLabel}>
              {t("reminders.reminderTimesLabel")}
            </span>
            <div className={styles.timeSlotBox}>
              <div className={styles.timeSlotRow}>
                {timeSlots.map((slot, index) => (
                  <TimeChip
                    key={index}
                    value={slot.value}
                    onChange={slot.onChange}
                    t={t}
                  />
                ))}
              </div>
            </div>
          </div>
          {showEndDate &&
            paintReminderEndDateField(
              reminderEndDate,
              setReminderEndDate,
              maxDate,
              reminderDate,
              t,
              labelKey,
              true,
              true
            )}
          {paintReminderNotesField(
            notes,
            setNotes,
            t,
            labelKey,
            true,
            undefined,
            true
          )}
        </div>
      </div>
      {/* Portalled straight to <body> — this screen renders inside
          .profile-page-root, which sets transform: translateZ(0) for iOS
          keyboard-flicker prevention. A transformed ancestor becomes the
          containing block for any position:fixed descendant, so
          .confirm-footer's "bottom: 0" would resolve against that ancestor's
          own (content-height-tall) box instead of the viewport — pinning it
          past the end of the scrollable content, off-screen, rather than to
          the visible bottom of the screen. Rendering outside that ancestor
          entirely is what actually fixes it to the viewport. */}
      {createPortal(
        <div className="confirm-footer">
          <MButton
            variant="filled"
            size="lg"
            onClick={onSubmit}
            className="w-full"
            disabled={isInvalid}
          >
            {submitLabel || t("reminders.setReminder")}
          </MButton>
        </div>,
        document.body
      )}
    </div>
  );
};

export default ReminderSummaryScreen;
